/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    TextField,
    Alert,
    Typography,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Chip,
    IconButton,
    Tooltip,
    MenuItem,
} from "@mui/material";
import { Delete, PersonAddAlt1 } from "@mui/icons-material";

import {
    selectActiveBusinessId,
    selectActiveBusinessRole,
} from "../../redux/selectors/collectSelector";
import {
    inviteTeamMemberApi,
    listTeamInvitesApi,
    revokeTeamInviteApi,
    listTeamMembersApi,
    removeTeamMemberApi,
    updateTeamMemberRoleApi,
    ITeamInvite,
    ITeamMember,
} from "../../redux/services/businessTeamService";
import {
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
} from "./styles";
import { useToast } from "../../providers/ToastProvider";

/**
 * Translate a raw backend error message for the invite action into a clean,
 * friendly toast. The "already joined / accepted" 409s are expected outcomes
 * (not failures), so they surface as an info toast rather than an error.
 */
function friendlyInviteResult(
    raw: string,
    email: string,
): { severity: "info" | "warning" | "error"; title: string; message: string } {
    const t = (raw || "").toLowerCase();
    if (
        t.includes("already joined") ||
        t.includes("already a member") ||
        t.includes("already accepted") ||
        t.includes("already signed up")
    ) {
        return {
            severity: "info",
            title: "Already on your team",
            message: `${email} has already joined — no need to invite them again.`,
        };
    }
    if (t.includes("already registered") || t.includes("already exists")) {
        return {
            severity: "info",
            title: "Email already in use",
            message: `${email} already has an account. Ask them to sign in instead.`,
        };
    }
    if (t.includes("valid email")) {
        return {
            severity: "warning",
            title: "Check the email address",
            message: "Please enter a valid email address and try again.",
        };
    }
    return {
        severity: "error",
        title: "Couldn't send invite",
        message: raw || "Something went wrong. Please try again.",
    };
}

// ─────────────────────────────────────────────────────────────────────────
// Collect Team — business-admin manages teammates: pre-authorize emails
// (invites), view/remove active members, revoke pending invites.
// Teammates complete their own sign-up at /collect/team-sign-up using the
// business ID + the invited email.
// ─────────────────────────────────────────────────────────────────────────

const CollectTeam: React.FC = () => {
    const businessId = useSelector(selectActiveBusinessId);
    const role = useSelector(selectActiveBusinessRole);
    const isAdmin = role === "owner" || role === "admin";
    // Only the business owner can promote/demote teammates.
    const isOwner = role === "owner";
    const toast = useToast();

    const [members, setMembers] = useState<ITeamMember[]>([]);
    const [invites, setInvites] = useState<ITeamInvite[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [inviteEmail, setInviteEmail] = useState("");
    const [inviting, setInviting] = useState(false);
    const [savingRoleId, setSavingRoleId] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        setError(null);
        try {
            const [m, i] = await Promise.all([
                listTeamMembersApi(businessId),
                isAdmin ? listTeamInvitesApi(businessId) : Promise.resolve([]),
            ]);
            setMembers(m);
            setInvites(i);
        } catch (e: any) {
            setError(e.message || "Failed to load team.");
        } finally {
            setLoading(false);
        }
    }, [businessId, isAdmin]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!businessId) return;
        const email = inviteEmail.trim().toLowerCase();
        setInviting(true);
        try {
            await inviteTeamMemberApi(businessId, email);
            setInviteEmail("");
            toast.success(
                `Invitation sent to ${email}. They'll get an email with a link to join.`,
                { title: "Invite sent" },
            );
            await refresh();
        } catch (e: any) {
            const f = friendlyInviteResult(e?.message || "", email);
            toast[f.severity](f.message, { title: f.title });
            // An "already joined" outcome means our member/invite lists are
            // stale — refresh so the admin sees the current state.
            if (f.severity !== "error") {
                await refresh();
            }
        } finally {
            setInviting(false);
        }
    };

    const handleRevoke = async (inviteId: string) => {
        if (!businessId) return;
        try {
            await revokeTeamInviteApi(businessId, inviteId);
            toast.success("Invitation revoked.");
            await refresh();
        } catch (e: any) {
            toast.error(e?.message || "Failed to revoke invite.", {
                title: "Couldn't revoke invite",
            });
        }
    };

    const handleRemove = async (membershipId: string) => {
        if (!businessId) return;
        try {
            await removeTeamMemberApi(businessId, membershipId);
            toast.success("Team member removed.");
            await refresh();
        } catch (e: any) {
            toast.error(e?.message || "Failed to remove member.", {
                title: "Couldn't remove member",
            });
        }
    };

    const handleRoleChange = async (
        membershipId: string,
        newRole: "admin" | "staff",
    ) => {
        if (!businessId) return;
        setSavingRoleId(membershipId);
        try {
            await updateTeamMemberRoleApi(businessId, membershipId, newRole);
            toast.success(
                `Role updated to ${newRole === "admin" ? "Admin" : "Staff"}.`,
                { title: "Role updated" },
            );
            await refresh();
        } catch (e: any) {
            toast.error(e?.message || "Failed to update role.", {
                title: "Couldn't update role",
            });
            // Re-sync the list so the dropdown reverts to the real value.
            await refresh();
        } finally {
            setSavingRoleId(null);
        }
    };

    if (!businessId) {
        return (
            <CollectSectionCard>
                <Typography>Select a business to manage your team.</Typography>
            </CollectSectionCard>
        );
    }

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
            <CollectSectionCard sx={{ mb: 3 }}>
                <CollectSectionTitle>Team</CollectSectionTitle>
                <CollectSectionSubtitle sx={{ wordBreak: "break-word" }}>
                    Invite teammates by email — they'll get a link to join. Your
                    business ID is <strong>{businessId}</strong> if they prefer
                    to sign up manually.
                </CollectSectionSubtitle>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {isAdmin && (
                    <Box
                        component="form"
                        onSubmit={handleInvite}
                        sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            flexDirection: { xs: "column", sm: "row" },
                            mt: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <TextField
                            label="Teammate email"
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            required
                            size="small"
                            sx={{
                                width: { xs: "100%", sm: 280 },
                                minWidth: 0,
                            }}
                        />
                        <CollectPrimaryButton
                            type="submit"
                            disabled={inviting}
                            startIcon={
                                inviting ? undefined : <PersonAddAlt1 />
                            }
                            sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                            {inviting ? (
                                <CircularProgress
                                    size={20}
                                    sx={{ color: "#fff" }}
                                />
                            ) : (
                                "Invite"
                            )}
                        </CollectPrimaryButton>
                    </Box>
                )}
            </CollectSectionCard>

            <CollectSectionCard sx={{ mb: 3 }}>
                <CollectSectionTitle>Members</CollectSectionTitle>
                {loading ? (
                    <Box sx={{ py: 3, textAlign: "center" }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 560 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    {isAdmin && <TableCell align="right" />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.map((m) => (
                                    <TableRow key={m._id}>
                                        <TableCell>
                                            {m.businessUserId
                                                ? `${m.businessUserId.firstName} ${m.businessUserId.lastName}`
                                                : "—"}
                                        </TableCell>
                                        <TableCell sx={{ wordBreak: "break-word" }}>
                                            {m.businessUserId?.email || "—"}
                                        </TableCell>
                                        <TableCell>
                                            {isOwner && m.role !== "owner" ? (
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={m.role}
                                                    onChange={(e) =>
                                                        handleRoleChange(
                                                            m._id,
                                                            e.target.value as
                                                            | "admin"
                                                            | "staff",
                                                        )
                                                    }
                                                    disabled={
                                                        savingRoleId === m._id
                                                    }
                                                    sx={{ minWidth: 110 }}
                                                >
                                                    <MenuItem value="admin">
                                                        Admin
                                                    </MenuItem>
                                                    <MenuItem value="staff">
                                                        Staff
                                                    </MenuItem>
                                                </TextField>
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label={
                                                        m.businessRoleId
                                                            ?.roleName || m.role
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {m.businessUserId?.status || "—"}
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell align="right">
                                                {m.role !== "owner" && (
                                                    <Tooltip title="Remove member">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleRemove(m._id)
                                                            }
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {members.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 5 : 4}>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "text.secondary" }}
                                            >
                                                No team members yet.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CollectSectionCard>

            {isAdmin && (
                <CollectSectionCard>
                    <CollectSectionTitle>Pending invites</CollectSectionTitle>
                    <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 480 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invites.map((inv) => (
                                    <TableRow key={inv._id}>
                                        <TableCell sx={{ wordBreak: "break-word" }}>
                                            {inv.email}
                                        </TableCell>
                                        <TableCell>{inv.roleName}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={inv.status} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Revoke invite">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleRevoke(inv._id)
                                                    }
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {invites.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "text.secondary" }}
                                            >
                                                No pending invites.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CollectSectionCard>
            )}
        </Box>
    );
};

export default CollectTeam;
